'use client'

import React from 'react'

import { Button } from '../../components/Button'
import { Loading } from '../../components/Loading'
import { Modal, ModalProps } from '../../components/Modal'
import { Arrow, Assignment } from '../../icons'
import { formatNumber } from '../../utils/formatNumber'
import { getBonusRate } from '../../utils/getBonusRate'
import { Notes } from '../PointPurchaseModal/Notes'
import { PlanCard } from '../PointPurchaseModal/PlanCard'
import { PurchaseSummary } from '../PointPurchaseModal/PurchaseSummary'
import { SubscriptionPlanItem } from '../PointPurchaseModal/types'
import { TimingOption } from './TimingOption'

export { TimingOption, type TimingOptionProps } from './TimingOption'

/**
 * When the new plan takes effect. `immediate` charges the price difference
 * now; `next_cycle` switches at the next payment without charging now.
 */
export type PlanChangeTiming = 'immediate' | 'next_cycle'

const DEFAULT_SUBSCRIPTION_SERVICE_URL = 'https://service.gyym.jp/subscription'
const DEFAULT_SUBSCRIPTION_SUPPORT_URL =
  'https://support.gyym.jp/hc/ja/categories/29544675492505'

const timingName = 'plan-change-timing'
const timingHeadingId = 'plan-change-timing-heading'

// Plans are laid out in a 2-column grid (`grid-cols-2` below)
const COLUMNS = 2

export interface PlanChangeModalProps extends Omit<ModalProps, 'title'> {
  /**
   * Renders a spinner in place of the body
   */
  isLoading?: boolean

  /**
   * Plans in display order, including the current one
   */
  plans?: SubscriptionPlanItem[]

  /**
   * The plan the user is subscribed to. Rendered with the ご利用中のプラン
   * label and cannot be selected.
   */
  currentPlanId?: string

  selectedPlanId?: string | null

  onSelectedPlanChange?: (planId: string | null) => void

  /**
   * Chosen timing. Only asked for upgrades — a downgrade always takes effect
   * from the next cycle.
   */
  timing?: PlanChangeTiming | null

  onTimingChange?: (timing: PlanChangeTiming) => void

  /**
   * First day of the next cycle, pre-formatted (e.g. `11月12日`)
   */
  nextCycleDate?: string

  /**
   * Expiry of the points granted by an immediate upgrade, pre-formatted
   * (e.g. `2019/12/18`)
   */
  expireDate?: string

  /**
   * Card information section. Injected by the app so this component stays free
   * of payment concerns.
   */
  cardSection?: React.ReactNode

  isSubmitting?: boolean

  /**
   * Blocks the submission for reasons outside this component, such as there
   * being no card to authenticate
   */
  isSubmitDisabled?: boolean

  onSubmit?: () => void

  subscriptionServiceUrl?: string

  subscriptionSupportUrl?: string
}

/**
 * Subscription plan change modal.
 *
 * Presentational only — plan and timing selection are controlled, and the card
 * section is injected through `cardSection`.
 */
export const PlanChangeModal = ({
  isLoading,
  plans = [],
  currentPlanId,
  selectedPlanId = null,
  onSelectedPlanChange,
  timing = null,
  onTimingChange,
  nextCycleDate,
  expireDate,
  cardSection,
  isSubmitting,
  isSubmitDisabled,
  onSubmit,
  subscriptionServiceUrl = DEFAULT_SUBSCRIPTION_SERVICE_URL,
  subscriptionSupportUrl = DEFAULT_SUBSCRIPTION_SUPPORT_URL,
  onClose,
  ...props
}: PlanChangeModalProps) => {
  const currentPlan = plans.find((plan) => plan.id === currentPlanId)
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId)

  const isUpgrade =
    !!selectedPlan && !!currentPlan && selectedPlan.price > currentPlan.price

  // A downgrade can only take effect from the next cycle
  const effectiveTiming: PlanChangeTiming | null = !selectedPlan
    ? null
    : isUpgrade
    ? timing
    : 'next_cycle'

  const canSubmit = !!effectiveTiming && !isSubmitDisabled

  const labelOf = (plan: SubscriptionPlanItem) =>
    plan.id === currentPlanId
      ? 'ご利用中のプラン'
      : plan.isMostPopular
      ? '人気No.1'
      : undefined

  // Figma aligns titles across a row: when a card carries a label, the other
  // card in the same row reserves the label's space as well
  const rowOf = (index: number) => Math.floor(index / COLUMNS)
  const labelledRows = new Set(
    plans.map((plan, index) => (labelOf(plan) ? rowOf(index) : -1)),
  )

  return (
    <Modal title="定期プランを変更" {...props} onClose={onClose}>
      <Modal.Body className="flex justify-center !px-0 !pt-4 pc:w-[500px]">
        {isLoading && (
          <div className="flex items-center justify-center pc:min-h-[calc(70vh-118px)]">
            <Loading />
          </div>
        )}

        {!isLoading && (
          <div className="flex w-full flex-col items-center gap-4">
            <div className="flex w-full flex-col items-center gap-4 px-4 pc:px-6">
              <div className="grid w-full grid-cols-2 gap-3">
                {plans.map((plan, index) => (
                  <PlanCard
                    key={plan.id}
                    type="subscription"
                    state={
                      plan.id === currentPlanId
                        ? 'current'
                        : !plan.isAvailable
                        ? 'unavailable'
                        : plan.id === selectedPlanId
                        ? 'selected'
                        : 'default'
                    }
                    label={labelOf(plan)}
                    reserveLabelSpace={labelledRows.has(rowOf(index))}
                    description={plan.description}
                    title={plan.name}
                    price={plan.price}
                    priceUnit="/月"
                    point={plan.purchasePoint + plan.bonusPoint}
                    bonusRate={getBonusRate(
                      plan.purchasePoint,
                      plan.bonusPoint,
                    )}
                    onClick={() =>
                      onSelectedPlanChange?.(
                        plan.id === selectedPlanId ? null : plan.id,
                      )
                    }
                  />
                ))}
              </div>

              <a
                href={subscriptionServiceUrl}
                target="_blank"
                className="flex w-full items-center justify-between gap-2 bg-gray-06 px-4 py-3 text-blue-01 typography-body2"
                rel="noreferrer"
              >
                定期購入サービスについてはこちら
                <Arrow className="size-6 shrink-0" />
              </a>

              <Notes
                variant="full"
                subscriptionSupportUrl={subscriptionSupportUrl}
              />
            </div>

            {/*
              Mirrors what the API does: an immediate upgrade charges only the
              price difference and grants only the point difference, while a
              next-cycle change charges nothing now.
            */}
            {effectiveTiming === 'immediate' && selectedPlan && currentPlan ? (
              <PurchaseSummary
                value={selectedPlan.purchasePoint - currentPlan.purchasePoint}
                extra={selectedPlan.bonusPoint - currentPlan.bonusPoint}
                amount={selectedPlan.price - currentPlan.price}
                expireDate={expireDate}
              />
            ) : effectiveTiming === 'next_cycle' && selectedPlan ? (
              <PurchaseSummary
                value={selectedPlan.purchasePoint}
                extra={selectedPlan.bonusPoint}
                amount={0}
                note={`${nextCycleDate}に付与（有効期限: 付与日から90日）`}
                caption={`${nextCycleDate}から月々${formatNumber(
                  selectedPlan.price,
                )}円のご決済となります。本日のご決済はありません。`}
              />
            ) : (
              <PurchaseSummary expireDate={expireDate} />
            )}

            {cardSection}

            <div className="w-full">
              <div
                id={timingHeadingId}
                className="flex h-9 w-full items-center gap-2 bg-gray-01 px-3 text-white typography-subhead2"
              >
                <Assignment className="size-6 shrink-0" /> 新プランの適用
              </div>

              <div className="flex flex-col gap-3 px-6 pb-6 pt-4 text-gray-01 typography-body2">
                {!selectedPlan && <p>プランを選択してください</p>}

                {selectedPlan && isUpgrade && (
                  <div
                    role="radiogroup"
                    aria-labelledby={timingHeadingId}
                    className="flex flex-col gap-3"
                  >
                    <TimingOption
                      name={timingName}
                      value="immediate"
                      checked={timing === 'immediate'}
                      title="即日"
                      description="購入手続きを完了すると新プランのポイントが付与されます"
                      onChange={() => onTimingChange?.('immediate')}
                    />
                    <TimingOption
                      name={timingName}
                      value="next_cycle"
                      checked={timing === 'next_cycle'}
                      title="翌月"
                      description={`${nextCycleDate}に新プランのポイントが付与されます`}
                      onChange={() => onTimingChange?.('next_cycle')}
                    />
                  </div>
                )}

                {selectedPlan && !isUpgrade && (
                  <p>
                    翌月（{nextCycleDate}）から新プランのポイントが付与されます
                  </p>
                )}
              </div>
            </div>

            <div className="w-full px-4 pc:px-6">
              <Button
                variant="brand"
                size="lg"
                fullWidth
                loading={isSubmitting}
                disabled={!canSubmit}
                onClick={onSubmit}
              >
                {/* Every plan change goes through 3D Secure */}
                本人認証して変更する
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}
